import { Injectable, Injector } from '@angular/core';
import { Account } from './account.service';
import { Config } from './config';
import { Observable, Observer } from 'rxjs';
import { HttpService } from './http.service';
import { OAuthToken } from './oauth-token';
import { OAuthTokenRequest } from './oauth-token-request';
import { Registration } from './registration';
import { SftpRegistration } from './sftp_registration';
import { RegistrationReply } from './registration_reply';
import { Organization } from './organization';
import { AccountAssociation } from './account_association';
import { AccountAssociationReply } from './account_association_reply';
import { AccountAssociationRequest } from './account_association_request';
import { UserRoles } from './user_roles';
import { RegistrationFind } from './registration_find';
import { SchoolAccount } from './school_account';
import { AccountAssociationCountReply } from './account_association_count_reply';
import { RegistrationSyncReply } from './registration_sync_reply';
import { RegistrationRequest } from './registration_request';
import { RegistrationSyncRequest } from './registration_sync_request';

@Injectable()
export class OneRosterServiceMoc {
	userRolesKey: string = 'userRoles';
	timeout: number = 5000; //10 sec
	retry: number = 3; //max retry
	//delay:number = 10000; //max delay after retry
	errors: any = {};
	maxPollIteration: number = 5;
	// userAccountRoles: UserRoles;
	registrations: Registration[] = [];
	sftpRegistrations: SftpRegistration[] = [];

	constructor(private http: HttpService, private account: Account, private config: Config) {
		this.errors['default'] = 'There is an error when processing this request, please try again later.';
		this.errors['no connection'] = 'There is an error when communicating with a server, please try again later.'
		this.errors['unauthorized'] = 'Unauthorized access.';
		this.errors['not found'] = 'No results.';
		this.errors['invalid token'] = 'Unauthorized access.';
		this.errors['duplicate entry'] = 'This data already exists.';
		this.errors['bad request'] = 'Invalid request.';
		this.errors['internal server error'] = 'Internal server error. Please try again later.';
		this.errors['unexpected'] = 'Sorry, an unexpected error has occured. Please contact Tech Support.';
		this.errors['timeout'] = 'Timeout when communicating with a server, please try again later.';
		this.errors['email address is invalid'] = 'Email address is invalid.';
		this.errors['user status is invalid'] = 'Invalid user status.';
		this.errors['user status signup is invalid email not confirmed'] = 'The user you\'ve invited has not yet confirmed their email address.';
		this.errors['notification not supported'] = 'Push notifications are not supported in this browser.';
		this.errors['account license expired'] = 'Account License has expired. Please visit the Subscription page for more details and license renewal.';
		this.errors['invalid account license'] = 'Account License is invalid. Please visit the Subscription page for more details and license renewal.';
		this.errors['unauthorized access role'] = 'Unauthorized access role.';
		this.errors['oneroster license expired'] = 'OneRoster License has expired. Please visit the District Manager Subscription page for more details and license renewal.';
		this.errors['invalid oneroster license'] = 'OneRoster License is invalid. Please visit the District Manager Subscription page for more details and license renewal.';
	}

	validateAcctNumber(acctNumber: string): Observable<boolean> {
		let lusr = this.userRoles();
		if (lusr != null && lusr.acctNumber == acctNumber) {
			//return non http observable when the userroles exists at localStorage
			return Observable.create((observer: Observer<boolean>) => {
				observer.next(true);
				observer.complete();
			});
		}
		return Observable.create((observer: Observer<boolean>) => {
			observer.next(false);
			observer.complete();
		});
	}

	loadUser(): Observable<UserRoles> {
		let u = this.account.getUserProfileApi().subscribe(us => {
			this.account.setUser(us);
		});
		return this.userRolesApi().map(urs => {
			this.setUserRoles(urs[0]);
			return urs[0]
		}).do(ur => u);
	}

	loadUserByAcctNumber(acctNumber: string): Observable<UserRoles> {
		let u = this.account.getUserProfileApi().subscribe(us => {
			this.account.setUser(us);
		});
		return this.userRolesApi().map(urs => {
			for (let a of urs) {
				if (a.acctNumber === acctNumber) {
					this.setUserRoles(a);
					return a;
				}
			}
			throw new Error('unauthorized');
		})
		.do(ur => u);
	}


	removeLocalStorage() {
		localStorage.removeItem(this.userRolesKey);
	}

	setUserRoles(us: UserRoles) {
		localStorage.setItem(this.userRolesKey, JSON.stringify(us));
	}

	userRoles() {
		if (localStorage.getItem(this.userRolesKey) === null) {
			return null
		}
		return JSON.parse(localStorage.getItem(this.userRolesKey));
	}

	getToken() {
		return this.account.getToken();
	}

	getError(err: any): Error {
		var er: any;
		var msg = 'no connection';
		if (typeof (err.json) === 'function') {
			er = err.json();
			if (er.message != null && er.message != null && er.message != '') {
				msg = er.message;
			} else {
				console.log(err);
			}
			return new Error(msg);
		} else if (err instanceof Error) {
			return err;
		} else {
			console.log(err);
		}
	}

	getUserError(err: Error): string {
		return this.errors[err.message];
	}

	isErrNotFound(err: Error): boolean {
		return err.message == 'not found';
	}

	isErrUnauthorized(err: Error): boolean {
		return err.message == 'unauthorized' ||
			err.message == 'unauthorized access role';
	}

	getUserErrorDefault(err: Error): string {
		let e = this.errors[err.message];
		if (e != null) {
			return e;
		}
		return this.errors['default'];
	}

	buildUrl(parameters) {
		var qs = "";
		for (var key in parameters) {
			var value = parameters[key];
			if (value != null && value != '') {
				qs += encodeURIComponent(key) + "=" + encodeURIComponent(value) + "&";
			}
		}
		if (qs.length > 0) {
			return qs.substring(0, qs.length - 1); //chop off last "&"
		}
		return '';
	}

	params(parameters) {
		var params: any = {}
		for (var key in parameters) {
			var value = parameters[key];
			if (value != null && value != '') {
				params[encodeURIComponent(key)] = encodeURIComponent(value);
			}
		}
		return params;
	}

	leftPad(val: string, size: number, ch: string): string {
		var result = String(val);
		if (!ch) {
			ch = " ";
		}
		while (result.length < size) {
			result = ch + result;
		}
		return result;
	}

	hexAcctNumber(num: number): string {
		//sign >>> is Zero-fill right shift
		return this.leftPad((num >>> 0).toString(16), 8, '0').toUpperCase();
	}

	// APIs
	userRolesApi(): Observable<UserRoles[]> {
		var rows: UserRoles[] = [];
		rows.push({ acctNumber: 'A12F2343', schoolName: 'High School', isDefault: false, roles: ['admin', 'user'] });
		rows.push({ acctNumber: 'A12F2344', schoolName: 'College', isDefault: false, roles: ['admin', 'user'] });

		return Observable.create((observer: Observer<UserRoles[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	districtAccountAssociationFindApi(): Observable<SchoolAccount[]> {
		let rows: SchoolAccount[] = [];
		rows.push({
			id: 1,
			acctNumber: -1123433,
			organizationName: 'IAK School 1',
			parentAcctNumber: 'B851D6D9',
			enabled: true,
		});

		rows.push({
			id: 2,
			acctNumber: -2223455,
			organizationName: 'IAK School 2',
			parentAcctNumber: 'B851D6D9',
			enabled: true,
		});

		rows.push({
			id: 3,
			acctNumber: -1123456,
			organizationName: 'IAK School 3',
			parentAcctNumber: 'B851D6D9',
			enabled: true,
		});

		return Observable.create((observer: Observer<SchoolAccount[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	setDefaultApi(acctNumber: string): Observable<boolean> {
		return Observable.create((observer: Observer<boolean>) => {
			observer.next(true);
			observer.complete();
		});
	}


	// Registration
	registrationFindApi(acctNumber: string, req: RegistrationFind): Observable<Registration[]> {
		let rows: Registration[] = [];

		if (this.registrations.length === 0) {
			let r: Registration = {
				id: 1,
				oneRosterApiUrl: 'onerosterapiurl.net/api',
				oneRosterApiVersion: '1.1',
				oauthVersion: 'oauth-1.0-hmac-sha256',
				oauthClientId: '1a2b3c4d',
				oauthClientSecret: '123456',
				syncEnabled: true,
				syncInitialized: false,
				syncStatus: '',
				lastSyncError: '',
				lastSyncErrorAt: null,
				lastSyncedAt: null,
				syncCrunJob: '0 22 * * *',
				organizationCount: 0,
				studentCount: 0,
				studentContactCount: 0,
				createdAt: new Date(),
				updatedAt: null,
				oauthTokenEndpoint:'',
			};
			this.registrations.push(r);
		}


		for (let r of this.registrations) {

			if (r.id && r.id === req.id) {
				rows.push(r);
				break;
			}
			rows.push(r);
		}

		return Observable.create((observer: Observer<Registration[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	registrationAddApi(acctNumber: string, req: RegistrationRequest): Observable<RegistrationReply> {
		let row: RegistrationReply = {
			id: this.registrations.length + 1,
		}
		let r: Registration = {
			id: row.id,
			oneRosterApiUrl: req.oneRosterApiUrl,
			oneRosterApiVersion: req.oneRosterApiVersion,
			oauthVersion: req.oauthVersion,
			oauthClientId: req.oauthClientId,
			oauthClientSecret: req.oauthClientSecret,
			syncCrunJob: req.syncCrunJob,
			syncEnabled: true,
			syncInitialized: false,
			syncStatus: '',
			lastSyncError: '',
			lastSyncErrorAt: null,
			lastSyncedAt: null,
			organizationCount: 0,
			studentCount: 0,
			studentContactCount: 0,
			createdAt: new Date(),
			updatedAt: null,
			oauthTokenEndpoint: req.oauthTokenEndpoint,
		};
		this.registrations.push(r);
		return Observable.create((observer: Observer<RegistrationReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	registrationUpdateApi(acctNumber: string, id: number, req: RegistrationRequest): Observable<RegistrationReply> {
		let row: RegistrationReply = {
			id: id,
		}

		for (let r of this.registrations) {
			if (r.id === id) {
				r.oneRosterApiUrl = req.oneRosterApiUrl;
				r.oneRosterApiVersion = req.oneRosterApiVersion;
				r.oauthVersion = req.oauthVersion;
				r.oauthClientId = req.oauthClientId;
				r.oauthClientSecret = req.oauthClientSecret;
				r.syncCrunJob = req.syncCrunJob;
				break;
			}
		}

		return Observable.create((observer: Observer<RegistrationReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	registrationRemoveApi(acctNumber: string, id: number): Observable<RegistrationReply> {
		let row: RegistrationReply = {
			id: 1,
		}

		for (let i = 0; i < this.registrations.length; i++) {
			if (this.registrations[i].id === id) {
				this.registrations = this.registrations.splice(i, 1);
			}
		}
		return Observable.create((observer: Observer<RegistrationReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	// SFTP Registration
	sftpRegistrationFindApi(acctNumber: string, req: any): Observable<SftpRegistration[]> {
		let rows: SftpRegistration[] = [];
		let acctInt = parseInt(acctNumber);

		if (this.sftpRegistrations.length === 0) {
			let r = new SftpRegistration();
			r.id = 1;
			r.districtAcctNumber = acctInt;
			r.syncStatus = 'Stopped';
			r.sftpConfig = {
				client: { host: 'sftp.example.com', port: 22, username: 'user1' },
				file: { remotePath: '/outbox', fileNamePattern: '*.csv' }
			};
			this.sftpRegistrations.push(r);
		}

		for (let r of this.sftpRegistrations) {
			if (r.districtAcctNumber === acctInt) {
				if (req.id && r.id === req.id) {
					rows.push(r);
					break;
				}
				if (!req.id) {
					rows.push(r);
				}
			}
		}

		return Observable.create((observer: Observer<SftpRegistration[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	sftpRegistrationAddApi(acctNumber: string, req: SftpRegistration): Observable<any> {
		req.id = this.sftpRegistrations.length + 1;
		this.sftpRegistrations.push(req);
		return Observable.of({ id: req.id });
	}

	sftpRegistrationUpdateApi(acctNumber: string, req: SftpRegistration): Observable<any> {
		for (let i = 0; i < this.sftpRegistrations.length; i++) {
			if (this.sftpRegistrations[i].id === req.id) {
				this.sftpRegistrations[i] = req;
				break;
			}
		}
		return Observable.of({ id: req.id });
	}

	sftpRegistrationRemoveApi(acctNumber: string, req: any): Observable<any> {
		for (let i = 0; i < this.sftpRegistrations.length; i++) {
			if (this.sftpRegistrations[i].id === req.id) {
				this.sftpRegistrations.splice(i, 1);
				break;
			}
		}
		return Observable.of({ id: req.id });
	}

	sftpTestConnectionApi(acctNumber: string, req: SftpRegistration): Observable<string[]> {
		let rows: string[] = [];
		for (let i = 1; i <= 10; i++) {
			rows.push(`directory_${i}/`);
		}
		return Observable.of(rows);
	}

	sftpGenerateCredentialsApi(acctNumber: string): Observable<{ host: string; port: number; username: string; password: string }> {
		return Observable.of({
			host: 'ssh.dev.identakid.net',
			port: 7000,
			username: 'mock-uuid-username-1234',
			password: 'MockGeneratedPassword123456789'
		});
	}

	sftpRegistrationSyncApi(acctNumber: string, id: number, req: RegistrationSyncRequest): Observable<RegistrationSyncReply> {
		let row: RegistrationSyncReply = {}

		for (let r of this.sftpRegistrations) {
			if (r.id === id) {
				r.syncCrunJob = req.syncCrunJob;
				r.syncStatus = req.status;

				setTimeout(() => {
					switch (req.status) {
						case 'stopping':
							r.syncStatus = 'stopped';
							r.lastSyncedAt = new Date();
							break;
						case 'starting':
							r.syncStatus = 'started';
							r.lastSyncedAt = new Date();
							break;
					}
				}, 1000);

				break;
			}
		}

		return Observable.create((observer: Observer<RegistrationSyncReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	registrationSyncApi(acctNumber: string, id: number, req: RegistrationSyncRequest): Observable<RegistrationSyncReply> {
		let row: RegistrationSyncReply = {}

		for (let r of this.registrations) {
			if (r.id === id) {
				r.studentContactCount = 10;
				r.studentCount = 5;
				r.organizationCount = 20;
				r.syncCrunJob = req.syncCrunJob;
				r.syncStatus = req.status;

				setTimeout(() => {
					switch (req.status) {
						case 'stopping':
							r.syncStatus = 'stopped';
							r.lastSyncedAt = new Date();
							break;
						case 'starting':
							r.syncStatus = 'started';
							r.lastSyncedAt = new Date();

							setTimeout(() => {
								r.lastSyncError = 'some error from syncing';
								r.syncStatus = 'error';
								r.lastSyncedAt = new Date();
							}, 2000);
							break;
						case 'initializing':
							r.syncStatus = 'initialized';
							r.syncInitialized = true;
							r.lastSyncedAt = new Date();

							setTimeout(() => {
								r.lastSyncError = 'some error from initializing';
								r.syncStatus = 'error-initializing';
								r.lastSyncedAt = new Date();
							}, 2000);

							// setTimeout(() => {
							// 	r.lastSyncError = 'some error from initializing';
							// 	r.syncStatus = 'disabled';
							// 	r.lastSyncedAt = new Date();
							// }, 3000);
							break;
					}
				}, 1000);


				break;
			}
		}

		return Observable.create((observer: Observer<RegistrationSyncReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	// Organization
	organizationFindApi(acctNumber: string): Observable<Organization[]> {
		let rows: Organization[] = [];

		rows.push({
			id: 1,
			districtAcctNumber: 'A851D4D9',
			registrationId: 1,
			sourceId: '65eb6c64-c437-4df6-84b1-ed42732a6b52',
			name: 'Greenfield University',
			active: true,
			createdAt: new Date(),
			updatedAt: new Date(),
		});

		rows.push({
			id: 2,
			districtAcctNumber: 'A851D4D9',
			registrationId: 1,
			sourceId: '2beb6c64-c437-4aa6-84b1-ed42732a6b4j',
			name: 'Eureka Institute',
			active: true,
			createdAt: new Date(),
			updatedAt: new Date(),
		});

		// rows.push({
		// 	id: 3,
		// 	districtAcctNumber: 'J821D6D9',
		// 	registrationId: 1,
		// 	sourceId: '2beb6c64-c437-4aa6-84b1-ed42732a6b4j',
		// 	name: 'Eureka Institute',
		// 	active: true,
		// 	createdAt: new Date(),
		// 	updatedAt: new Date(),
		// });

		// rows.push({
		// 	id: 3,
		// 	districtAcctNumber: 'A851D4D9',
		// 	registrationId: 1,
		// 	sourceId: '2beb6c64-c437-4aa6-84b1-ed42732a6b4j',
		// 	name: 'Eureka Institute',
		// 	active: true,
		// 	createdAt: new Date(),
		// 	updatedAt: new Date(),
		// });

		return Observable.create((observer: Observer<Organization[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	// Account Association
	accountAssociationFindApi(acctNumber: string): Observable<AccountAssociation[]> {
		let rows: AccountAssociation[] = [];

		rows.push({
			id: 5,
			districtAcctNumber: 'A651D3D2',
			registrationId: 1,
			acctNumber: -1123456,
			organizationId: 1,
			syncStudents: true,
			syncStudentContacts: true,
			syncEnabled: true,
			updatedAt: new Date(),
			createdAt: new Date(),
		});

		rows.push({
			id: 6,
			districtAcctNumber: 'A651D3D2',
			registrationId: 1,
			acctNumber: -2223455,
			organizationId: 2,
			syncStudents: true,
			syncStudentContacts: true,
			syncEnabled: true,
			updatedAt: new Date(),
			createdAt: new Date(),
		});

		return Observable.create((observer: Observer<AccountAssociation[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	accountAssociationFindCountApi(): Observable<AccountAssociationCountReply> {
		return Observable.create((observer: Observer<AccountAssociationCountReply>) => {
			let c = new AccountAssociationCountReply();
			observer.next(c);
			observer.complete();
		});
	}

	accountAssociationAddApi(acctNumber: string, req: AccountAssociationRequest): Observable<AccountAssociationReply> {
		let row: AccountAssociationReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<AccountAssociationReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	accountAssociationUpdateApi(acctNumber: string, id: number, req: AccountAssociationRequest): Observable<AccountAssociationReply> {
		let row: AccountAssociationReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<AccountAssociationReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	accountAssociationRemoveApi(acctNumber: string, id: number): Observable<AccountAssociationReply> {
		let row: AccountAssociationReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<AccountAssociationReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	countNotification: number = 0;

	isErrDuplicateEntry(err: Error): boolean {
		return err.message == 'duplicate entry';
	}

	isErrAccountLicenseExpired(err: Error): boolean {
		return err.message == 'account license expired';
	}

	isErrAccountLicenseInvalid(err: Error): boolean {
		return err.message == 'invalid account license';
	}

	getDefaultSaveMessage(): string {
		return "Successfully Saved";
	}

	oauthTokenApi(req: OAuthTokenRequest): Observable<OAuthToken> {
		return Observable.of(new OAuthToken());
	}

}
